import { getStore } from "@/client/store";
import {
  CreditCard,
  CURRENCIES,
  Currency,
  ISSUERS,
  Offer,
} from "@/data/schemas";
import {
  AVERAGE_VALUES,
  DEFAULT_VALUATIONS,
  DIRECT_CASHBACK_VALUATIONS,
  FREQUENT_MILER_VALUATIONS,
  NERDWALLET_BUSINESS_VALUATIONS,
  NERDWALLET_ECONOMY_VALUATIONS,
} from "@/data/valuations";
import { parseDate } from "@/utils/date";
import { offerMatchesFilters } from "@/utils/filter";

export const VALUATION_METHODS = {
  DEFAULT: "Default",
  CASH_REDEMPTION: "Pure Cashback",
  FREQUENT_MILER: "Frequent Miler's RVVs",
  NERDWALLET_ECONOMY: "NerdWallet Economy",
  NERDWALLET_BUSINESS: "NerdWallet Business",
  CUSTOM: "Custom",
};

const VALUATION_METHOD_TO_VALUATION_MAP: {
  [p: string]: { [p: string]: number };
} = {
  [VALUATION_METHODS.DEFAULT]: DEFAULT_VALUATIONS,
  [VALUATION_METHODS.CASH_REDEMPTION]: DIRECT_CASHBACK_VALUATIONS,
  [VALUATION_METHODS.FREQUENT_MILER]: FREQUENT_MILER_VALUATIONS,
  [VALUATION_METHODS.NERDWALLET_ECONOMY]: NERDWALLET_ECONOMY_VALUATIONS,
  [VALUATION_METHODS.NERDWALLET_BUSINESS]: NERDWALLET_BUSINESS_VALUATIONS,
};

/**
 * Returns the most value public offer for a given card.
 * @param filters Represents the current filters that are being applied.
 * @param offers Represents the list of offers we're trying to get the max value from.
 * @param card The card in question.
 * @returns An Offer object representing the most valuable offer from the provided list.
 */
export const getMaxValueOffer = (card: CreditCard): Offer | undefined => {
  const filters = getStore().filters;
  const filteredOffers = card.offers
    .filter((offer) => offerMatchesFilters(offer, filters, card))
    .filter(
      (offer) =>
        !offer.expiration ||
        (offer.expiration && parseDate(offer.expiration) > new Date()),
    );
  if (filteredOffers.length === 0) {
    console.warn(`No valid offers found for card ${card.issuer} ${card.name}.`);
    return;
  }
  return filteredOffers.reduce((maxOffer: Offer | undefined, offer: Offer) => {
    if (!maxOffer) return offer;
    const offerValue = getOfferFirstYearValue(offer, card).value;
    const maxOfferValue = getOfferFirstYearValue(maxOffer, card).value;
    return offerValue > maxOfferValue ? offer : maxOffer;
  }, filteredOffers[0]);
};

/**
 * Returns the cents per point of the given currency at the given valuation method.
 * @param currency The currency to calculate the cents per point of.
 * @param valuationMethod Which VALUATION_METHODS to use in the calculation.
 * @returns The value, in cents, of the provided currency at the given valuation method.
 */
export const getPointsValue = (currency: Currency): number => {
  const filters = getStore().filters;
  if (currency === "USD") return 100;

  if (filters.valuationMethod === VALUATION_METHODS.CUSTOM) {
    return filters.customValuations[currency] ?? 0;
  }

  // If currency exists in map, return, else return from averages
  const map = VALUATION_METHOD_TO_VALUATION_MAP[filters.valuationMethod];
  if (!map) {
    throw new Error(
      `No map found for valuation method ${filters.valuationMethod}.`,
    );
  }
  const value = map[currency];
  if (value) return value;

  if (!AVERAGE_VALUES[currency]) {
    console.warn(
      `No average value found for currency ${currency}. Defaulting to 1cpp.`,
    );
    return 1;
  }

  return AVERAGE_VALUES[currency];
};

export const getOfferFirstYearValue = (
  offer: Offer,
  card: CreditCard,
): { value: number; explanation: string } => {
  const filters = getStore().filters;
  if (!offer)
    return {
      explanation:
        "No valid offers. This usually means one expired recently. The new one will be added soon!",
      value: 0,
    };
  // Calculate sum of all amounts
  let explanation = "";
  const cardCurrencyValue = getPointsValue(card.currency);
  let pointsValue = 0;
  explanation += "Points Value: ";
  for (const amount of offer.amount) {
    const offerCurrencyValue = getPointsValue(amount.currency ?? card.currency);
    pointsValue += (amount.amount * offerCurrencyValue) / 100;
    explanation += `\n+$${((amount.amount * offerCurrencyValue) / 100).toFixed(
      0,
    )} from ${amount.amount.toLocaleString()} ${
      amount.currency ?? card.currency
    } @ ${offerCurrencyValue.toFixed(2)}¢/point`;
  }

  // TypeScript is the reason I can't just do [Amex, Chase, Citi, USBank].includes(card.issuer)
  // SEE: https://fettblog.eu/typescript-array-includes/
  const firstYearAnnualFee =
    card.isAnnualFeeWaived ||
    (filters.isMilitary === "Yes" &&
      (card.issuer === ISSUERS.AMERICAN_EXPRESS ||
        card.issuer === ISSUERS.CHASE ||
        card.issuer === ISSUERS.CITI ||
        card.issuer === ISSUERS.US_BANK))
      ? 0
      : card.annualFee;
  if (firstYearAnnualFee !== 0) {
    explanation += `\n\nAnnual Fee: \n-$${firstYearAnnualFee} Annual Fee`;
  }

  let creditsValue = 0;
  if (filters.valuationMethod !== VALUATION_METHODS.CASH_REDEMPTION) {
    if ([...card.credits, ...(offer.credits ?? [])].length > 0)
      explanation += "\n\nCredits (weighted based on ease of use):";
    for (const credit of [...card.credits, ...(offer.credits ?? [])].sort(
      (a, b) => {
        return b.value * b.weight - a.value * a.weight > 0 ? 1 : -1;
      },
    )) {
      const currencyValue = getPointsValue(credit.currency ?? "USD") / 100;
      creditsValue += credit.value * currencyValue * credit.weight;
      explanation += `\n+$${(
        credit.value *
        currencyValue *
        credit.weight
      ).toFixed(0)}: ${credit.description} ($${
        credit.value * currencyValue
      } Value,  ${credit.weight * 100}% Weight)`;
    }
  }

  let valueGenerated: number;
  let valueMissed: number;
  if (offer.spend) {
    explanation += "\n\nOpportunity Cost Gained / Lost:";
    valueGenerated = // Value that the universal cashback on this card generates
      (offer.spend *
        (card.universalCashbackPercent / 100) *
        cardCurrencyValue) /
      100;
    if (card.currency !== "USD") valueGenerated *= 100;
    explanation += `\n+$${valueGenerated.toFixed(0)} value (${
      cardCurrencyValue === 100 ? 1 : cardCurrencyValue
    }¢/point) from spend on card`;
    const universalCashbackPointValue =
      filters.currentUniversalCashbackCurrency === CURRENCIES.USD
        ? 1
        : getPointsValue(filters.currentUniversalCashbackCurrency);
    valueMissed =
      offer.spend *
      ((filters.currentUniversalCashback ?? 0) / 100) *
      universalCashbackPointValue;
    explanation += `\n-$${valueMissed.toFixed(0)} value from not using a ${
      filters.currentUniversalCashback ?? 0
    }% universal cashback card with currency ${
      filters.currentUniversalCashbackCurrency
    } (${universalCashbackPointValue}¢/point) instead`;
  } else {
    valueGenerated = 0;
    valueMissed = 0;
  }

  return {
    value:
      Math.round(
        Math.round(
          pointsValue +
            valueGenerated -
            valueMissed -
            firstYearAnnualFee +
            creditsValue,
        ) / 10,
      ) * 10,
    explanation,
  };
};

/**
 * Calculates the percent return of the provided card at the provided valuation method.
 * @param filters Represents the current filters that are being applied.
 * @param offer The offer we're calculating percent return for.
 * @param card The card in question.
 * @returns A [0, 100] percentage return value.
 */
export const getPercentReturn = (offer: Offer, card: CreditCard) => {
  const firstYearValue = getOfferFirstYearValue(offer, card).value;
  const requiredSpend =
    offer.spend + (card.isAnnualFeeWaived ? 0 : card.annualFee);
  if (requiredSpend === 0) return 999_999_999; // Cards like the Lowes card don't have *any* spend; we should just return a high value, not an indefinite value
  return Math.round((firstYearValue / requiredSpend) * 100);
};

/**
 * Returns a [0, 1] value representing how good this offer is compared to any historical maxes with comparable spend.
 * @param card
 * @param valuationMethod
 * @param universalCashback
 * @returns Returns a [0, 1] value representing how good this offer is compared to any historical maxes at += 20% spend.
 */
export type PercentOfHistoricalMaxReturn = {
  value: number;
  bestHistoricalOffer?: Offer;
};

export const percentOfHistoricalMax = (
  offer: Offer,
  card: CreditCard,
): number => {
  if (!offer) return 0;
  const offerValue = getOfferFirstYearValue(offer, card).value;

  // Offers are in the "same ballpark" if they're both under 1k and within $100, or are within 25% of each other.
  if (!card.historicalOffers || card.historicalOffers.length === 0) return 100;
  const bestComparableHistoricalOffer = card.historicalOffers
    .filter((historicalOffer) => {
      return (
        (offer.spend < 1000 &&
          historicalOffer.spend < 1000 &&
          historicalOffer.spend - offer.spend <= 200) ||
        (historicalOffer.spend >= offer.spend * 0.6 &&
          historicalOffer.spend <= offer.spend * 1.4)
      );
    })
    .reduce((maxOffer, offer) => {
      const offerValue = getOfferFirstYearValue(offer, card).value;
      const maxOfferValue = getOfferFirstYearValue(maxOffer, card).value;
      return offerValue > maxOfferValue ? offer : maxOffer;
    }, offer);

  const bestHistoricalOfferValue = getOfferFirstYearValue(
    bestComparableHistoricalOffer,
    card,
  ).value;

  if (bestHistoricalOfferValue === 0) return 100;
  return offerValue / bestHistoricalOfferValue;
};

export const getCardValue = (card: CreditCard): number => {
  const maxValueOffer = getMaxValueOffer(card);
  if (!maxValueOffer) return 0;
  return getOfferFirstYearValue(maxValueOffer, card).value;
};
